/**
 * @overview higher order component to wrap components with the current `workspace` context
 */

import { withLoader } from 'evergreen-draft'
import withError from 'hoc/withError'
import withNotFoundHandler from 'hoc/withNotFoundHandler'
import getDisplayName from 'lib/get-display-name'
import { workspacesPath } from 'lib/path-helpers'
import React from 'react'
import { compose } from 'recompose'
import WorkspaceContext, { ContextProps } from './context'
import WorkspaceAccessDeniedApp from '../../containers/WorkspaceAccessDeniedApp'
import { trackAccessDenied } from 'lib/utils/errors'
import { Workspace } from './context'

interface InjectedContextProps
  extends Pick<ContextProps, 'accessDeniedReason' | 'isLoading' | 'workspace' | 'workspaceId' | 'refreshWorkspace'> {
  isErrored: boolean
  isFound: boolean
  workspaceSlug: string
}

function withWorkspaceContext<P extends InjectedContextProps>(Component: React.ComponentType<P>) {
  return class WithWorkspaceContext extends React.Component<Omit<P, keyof InjectedContextProps>> {
    static displayName = `WithWorkspaceContext(${getDisplayName(Component)})`

    render() {
      return (
        <WorkspaceContext.Consumer>
          {({
            error,
            accessDeniedReason,
            isLoading,
            isNotFound,
            workspace,
            workspaceId,
            workspaceSlug,
            refreshWorkspace
          }) => (
            <Component
              accessDeniedReason={accessDeniedReason}
              isErrored={Boolean(error)}
              isLoading={isLoading}
              isFound={!isNotFound}
              workspace={workspace}
              workspaceId={workspaceId}
              workspaceSlug={workspaceSlug}
              refreshWorkspace={refreshWorkspace}
              {...(this.props as P)}
            />
          )}
        </WorkspaceContext.Consumer>
      )
    }
  }
}

function withAccessDenied<P extends InjectedContextProps>(Component: React.ComponentType<P>) {
  const componentName = getDisplayName(Component)

  return class WithAccessDenied extends React.Component<P> {
    static displayName = `WithAccessDenied(${componentName})`

    componentDidMount() {
      this.maybeTrackAccessDenied(this.props.accessDeniedReason)
    }

    componentDidUpdate(prevProps: P) {
      if (prevProps.accessDeniedReason !== this.props.accessDeniedReason) {
        this.maybeTrackAccessDenied(this.props.accessDeniedReason)
      }
    }

    // If there's an access denied reason, or the reason changes we want to know
    maybeTrackAccessDenied = (reason?: string) => {
      if (reason) {
        trackAccessDenied({
          reason,
          workspaceSlug: this.props.workspaceSlug
        })
      }
    }

    render() {
      if (this.props.accessDeniedReason) {
        return (
          <WorkspaceAccessDeniedApp
            accessDeniedReason={this.props.accessDeniedReason}
            workspaceSlug={this.props.workspaceSlug}
          />
        )
      }

      return <Component {...this.props} />
    }
  }
}

// `withNotFoundHandler` checks for `path`'s truthiness, so we have to negate `isNotFound` for this to work right.
const withNotFound = withNotFoundHandler(({ workspaceSlug }) => ({
  path: 'isFound',
  title: 'Workspace not found',
  messaging: workspaceSlug ? `We couldn't find a workspace with the slug "${workspaceSlug}".` : null,
  primaryButton: {
    children: 'View my workspaces',
    onClick() {
      window.location.href = workspacesPath()
    }
  }
}))

const withErrorHandling = withError({
  path: 'isErrored',
  title: `Couldn't fetch workspace.`,
  messaging: 'An error occurred and we were unable to load your workspace.',
  primaryButton: {
    children: 'Try again',
    onClick: window.location.reload
  }
})

export interface WithWorkspaceContextProps extends InjectedContextProps {
  error: null
  workspace: Workspace
  workspaceId: string
}

/**
 * When you use this HOC you can expect your component should only render if:
 * - the workspace is found
 * - the user has access
 * - and didn't encounter any errors loading the graphql query
 *
 * The resulting props for your component are a bit more explicit (no Maybe types)
 */
export default function<P extends WithWorkspaceContextProps>(Component: React.ComponentType<P>) {
  return compose<P, Omit<P, keyof WithWorkspaceContextProps>>(
    // Get the context, and pass it down
    withWorkspaceContext,
    // Show a spinner?
    // @todo evaluate if we should let consumers decide what to render while loading
    // e.g. a spinner, or nothing at all
    withLoader,
    // Catch access denied errors
    withAccessDenied,
    // Catch "not found" workspaces
    withNotFound,
    // Catch all other errors
    withErrorHandling
  )(Component)
}
